import { z } from 'zod'

/**
 * Block documents (PLAN-079). A document is an ordered tree of blocks owned
 * by its DocumentStore Durable Object; editors, the agent, uploads and
 * migration all speak block *operations*. The block shape is BlockNote's
 * document JSON (stored as-is), typed loosely here because the editor owns
 * the per-type prop schemas.
 */

export const jsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(z.string(), jsonValueSchema),
  ])
)
export type JsonValue =
  string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue }

export interface DocBlock {
  id: string
  type: string
  props: Record<string, JsonValue>
  /** Inline content (styled text runs / links), table content, or undefined. */
  content?: JsonValue
  children: DocBlock[]
}

export const docBlockSchema: z.ZodType<DocBlock> = z.lazy(() =>
  z.object({
    id: z.string().min(1),
    type: z.string().min(1),
    props: z.record(z.string(), jsonValueSchema),
    content: jsonValueSchema.optional(),
    children: z.array(docBlockSchema),
  })
)

/** Inline content as BlockNote emits it — enough for conversions. */
export interface StyledText {
  type: 'text'
  text: string
  styles: Record<string, boolean | string>
}
export interface InlineLink {
  type: 'link'
  href: string
  content: StyledText[]
}
/**
 * A project member mentioned in the text (board 9s8r). A custom BlockNote
 * inline content (`content: 'none'`): plain JSON inside `block.content`, so
 * it rides block ops, revisions, undo and backups unchanged. In markdown it
 * is `[@Name](user:<userId>)` (worker/lib/blocks-markdown.ts).
 */
export interface InlineMention {
  type: typeof MENTION_INLINE_TYPE
  props: {
    userId: string
    /** Name at the time of writing — the chip shows the live name while the user is a member. */
    name: string
    /**
     * Per-mention id, the notification dedupe key (`MENTION_ID_RE`). The
     * editor generates it; the converter leaves it empty and the DocumentStore
     * assigns one (reusing the previous id for the same user in the same block).
     */
    id: string
  }
}
export type InlineContent = StyledText | InlineLink | InlineMention

export const MENTION_INLINE_TYPE = 'mention'
/** Client-generated per-mention id; the store assigns one when missing/invalid. */
export const MENTION_ID_RE = /^men_[a-z0-9]{10,40}$/
/** Markdown link target of a mention: `user:<userId>`. */
export const MENTION_HREF_RE = /^user:([A-Za-z0-9_-]+)$/

// ── Operations ────────────────────────────────────────────────────────────

export const blockOpSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('update'),
    id: z.string().min(1),
    /** Revision the client saw; the store rejects a stale update. Omitted = force. */
    baseRev: z.number().int().optional(),
    block: docBlockSchema,
  }),
  z.object({
    kind: z.literal('insert'),
    /** Insert after this sibling (null = first child of parent). */
    afterId: z.string().nullable(),
    /** Parent block (null = top level). */
    parentId: z.string().nullable(),
    blocks: z.array(docBlockSchema).min(1),
  }),
  z.object({
    kind: z.literal('delete'),
    ids: z.array(z.string().min(1)).min(1),
  }),
  z.object({
    kind: z.literal('move'),
    id: z.string().min(1),
    afterId: z.string().nullable(),
    parentId: z.string().nullable(),
  }),
])
export type BlockOp = z.infer<typeof blockOpSchema>

/** Who made a change: a user id or the agent. */
export const AGENT_ACTOR = 'hub'

export const blockRevSchema = z.object({ id: z.string(), rev: z.number().int() })

// ── WebSocket protocol (client ⇄ DocumentStore) ───────────────────────────

export const documentPresenceSchema = z.object({
  actor: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable().optional(),
  editingBlockIds: z.array(z.string()),
  updatedAt: z.string(),
})
export type DocumentPresence = z.infer<typeof documentPresenceSchema>

/** Name of the inline style that carries a comment thread's id (the range highlight). */
export const COMMENT_THREAD_STYLE = 'commentThread'
/** Client-generated thread ids (`comment:add` `id`); the store validates against this. */
export const COMMENT_THREAD_ID_RE = /^thr_[a-z0-9]{10,40}$/
export const COMMENT_BODY_MAX = 4000
export const COMMENT_QUOTE_MAX = 300

export const commentBodyFormatSchema = z.enum(['text', 'md'])
export type CommentBodyFormat = z.infer<typeof commentBodyFormatSchema>

/**
 * One comment. A thread is a root (`threadId === id`, `parentId === null`)
 * plus flat replies (`parentId` = root id). Quoted text and resolution
 * live on the root only.
 */
export const documentCommentSchema = z.object({
  id: z.string(),
  blockId: z.string(),
  authorId: z.string(),
  authorName: z.string(),
  body: z.string(),
  createdAt: z.string(),
  threadId: z.string(),
  parentId: z.string().nullable(),
  /** Old rows are plain text; the composer writes the markdown subset. */
  bodyFormat: commentBodyFormatSchema,
  /** The selected text at creation (root only; null = whole block). */
  quotedText: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  resolvedById: z.string().nullable(),
})
export type BlockComment = z.infer<typeof documentCommentSchema>

export const documentClientMessageSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('sync'), sinceRev: z.number().int().nullable() }),
  z.object({ t: z.literal('op'), clientSeq: z.number().int(), ops: z.array(blockOpSchema).min(1) }),
  z.object({ t: z.literal('presence'), editingBlockIds: z.array(z.string()) }),
  z.object({
    t: z.literal('comment:add'),
    /** Client thread id (root only) so the highlight can be applied before the round-trip. */
    id: z.string().regex(COMMENT_THREAD_ID_RE).optional(),
    blockId: z.string().min(1),
    body: z.string().min(1).max(COMMENT_BODY_MAX),
    bodyFormat: commentBodyFormatSchema.optional(),
    /** Root id of the thread this is a reply to. */
    parentId: z.string().min(1).optional(),
    quotedText: z.string().max(COMMENT_QUOTE_MAX).nullable().optional(),
  }),
  /** `id` is any comment in the thread; the whole thread is (un)resolved. */
  z.object({ t: z.literal('comment:resolve'), id: z.string().min(1), resolved: z.boolean() }),
  /** Own comments only; a root with replies is refused (`error` message). */
  z.object({ t: z.literal('comment:delete'), id: z.string().min(1) }),
])
export type DocumentClientMessage = z.infer<typeof documentClientMessageSchema>

export const documentServerMessageSchema = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('snapshot'),
    docRev: z.number().int(),
    blocks: z.array(docBlockSchema),
    revs: z.array(blockRevSchema),
    presence: z.array(documentPresenceSchema),
    comments: z.array(documentCommentSchema),
  }),
  z.object({
    t: z.literal('ops'),
    docRev: z.number().int(),
    actor: z.string(),
    ops: z.array(blockOpSchema),
    revs: z.array(blockRevSchema),
    /** Agent turn these ops belong to (for per-turn undo). */
    turnId: z.string().optional(),
  }),
  z.object({
    t: z.literal('ack'),
    clientSeq: z.number().int(),
    docRev: z.number().int(),
    revs: z.array(blockRevSchema),
  }),
  z.object({
    t: z.literal('reject'),
    clientSeq: z.number().int(),
    reason: z.string(),
    conflicts: z.array(
      z.object({ id: z.string(), current: docBlockSchema.nullable(), rev: z.number().int() })
    ),
  }),
  z.object({ t: z.literal('presence'), presence: z.array(documentPresenceSchema) }),
  z.object({ t: z.literal('comments'), comments: z.array(documentCommentSchema) }),
  z.object({ t: z.literal('error'), message: z.string() }),
])
export type DocumentServerMessage = z.infer<typeof documentServerMessageSchema>

// ── Helpers shared by client and worker ───────────────────────────────────

/** Plain text of a block's inline content (no formatting). */
export function blockPlainText(block: DocBlock): string {
  const content = block.content
  if (!content) return ''
  if (Array.isArray(content)) return inlineText(content as unknown as InlineContent[])
  if (typeof content === 'object' && (content as { type?: string }).type === 'tableContent') {
    const rows = ((content as { rows?: unknown[] }).rows ?? []) as Array<{ cells?: unknown[] }>
    return rows
      .map((row) =>
        (row.cells ?? [])
          .map((cell) => {
            const inline = Array.isArray(cell)
              ? (cell as InlineContent[])
              : ((cell as { content?: InlineContent[] })?.content ?? [])
            return inlineText(inline)
          })
          .join(' | ')
      )
      .join('\n')
  }
  return ''
}

export function inlineText(content: InlineContent[]): string {
  return content
    .map((c) =>
      c.type === 'text'
        ? c.text
        : c.type === 'link'
          ? inlineText(c.content)
          : c.type === MENTION_INLINE_TYPE
            ? `@${c.props.name}`
            : ''
    )
    .join('')
}

/** Every mention in this block's own content (table cells included; children excluded). */
export function mentionsIn(block: DocBlock): Array<InlineMention['props']> {
  const out: Array<InlineMention['props']> = []
  forEachInlineList(block, (runs) => {
    for (const run of runs) if (run.type === MENTION_INLINE_TYPE) out.push(run.props)
  })
  return out
}

/** Depth-first walk over a block tree. */
export function walkBlocks(
  blocks: DocBlock[],
  visit: (block: DocBlock, parent: DocBlock | null) => void
): void {
  const walk = (list: DocBlock[], parent: DocBlock | null) => {
    for (const block of list) {
      visit(block, parent)
      if (block.children.length > 0) walk(block.children, block)
    }
  }
  walk(blocks, null)
}

export function findBlock(blocks: DocBlock[], id: string): DocBlock | null {
  let found: DocBlock | null = null
  walkBlocks(blocks, (b) => {
    if (!found && b.id === id) found = b
  })
  return found
}

// ── Comment threads ───────────────────────────────────────────────────────

export interface CommentThread {
  /** Root comment id. */
  id: string
  blockId: string
  root: BlockComment
  /** Oldest first. */
  replies: BlockComment[]
  quotedText: string | null
  createdAt: string
  resolvedAt: string | null
  resolvedById: string | null
}

/**
 * Comments → threads (root + replies sorted by `createdAt`), threads sorted
 * by the root's `createdAt`. A reply whose root is gone is dropped (and
 * logged): it cannot be shown anywhere.
 */
export function groupThreads(comments: BlockComment[]): CommentThread[] {
  const byId = new Map<string, CommentThread>()
  for (const c of comments) {
    if (c.parentId !== null) continue
    byId.set(c.id, {
      id: c.id,
      blockId: c.blockId,
      root: c,
      replies: [],
      quotedText: c.quotedText,
      createdAt: c.createdAt,
      resolvedAt: c.resolvedAt,
      resolvedById: c.resolvedById,
    })
  }
  for (const c of comments) {
    if (c.parentId === null) continue
    const thread = byId.get(c.threadId) ?? byId.get(c.parentId)
    if (!thread) {
      console.warn(`[comments] reply ${c.id} has no root (${c.threadId}); dropped`)
      continue
    }
    thread.replies.push(c)
  }
  const byCreated = (a: { createdAt: string }, b: { createdAt: string }) =>
    a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0
  const threads = [...byId.values()]
  for (const t of threads) t.replies.sort(byCreated)
  return threads.sort(byCreated)
}

/** Distinct thread ids whose highlight occurs in this block's own content (children excluded). */
export function commentThreadIdsIn(block: DocBlock): string[] {
  const ids: string[] = []
  const visit = (runs: InlineContent[]) => {
    for (const run of runs) {
      if (run.type === 'link') {
        visit(run.content)
        continue
      }
      if (run.type === MENTION_INLINE_TYPE) continue
      const value = run.styles?.[COMMENT_THREAD_STYLE]
      if (typeof value === 'string' && value && !ids.includes(value)) ids.push(value)
    }
  }
  forEachInlineList(block, visit)
  return ids
}

/**
 * The block with the highlight of one thread removed (other styles, other
 * threads' highlights and links kept; adjacent runs that become identical
 * are merged). Children are returned untouched — strip them separately.
 */
export function stripCommentStyle(block: DocBlock, threadId: string): DocBlock {
  if (!commentThreadIdsIn(block).includes(threadId)) return block
  const strip = (runs: InlineContent[]): InlineContent[] =>
    mergeRuns(
      runs.map((run) => {
        if (run.type === 'link') return { ...run, content: strip(run.content) as StyledText[] }
        if (run.type === MENTION_INLINE_TYPE) return run
        if (run.styles?.[COMMENT_THREAD_STYLE] !== threadId) return run
        const styles = { ...run.styles }
        delete styles[COMMENT_THREAD_STYLE]
        return { ...run, styles }
      })
    )
  return mapInlineLists(block, strip)
}

/** Collapse adjacent text runs with identical styles. */
function mergeRuns(runs: InlineContent[]): InlineContent[] {
  const out: InlineContent[] = []
  for (const run of runs) {
    const prev = out[out.length - 1]
    if (
      prev &&
      prev.type === 'text' &&
      run.type === 'text' &&
      sameStyles(prev.styles ?? {}, run.styles ?? {})
    ) {
      out[out.length - 1] = { ...prev, text: prev.text + run.text }
    } else out.push(run)
  }
  return out
}

function sameStyles(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const ka = Object.keys(a)
  const kb = Object.keys(b)
  if (ka.length !== kb.length) return false
  return ka.every((k) => a[k] === b[k])
}

function isInlineList(value: unknown): value is InlineContent[] {
  return Array.isArray(value)
}

function forEachInlineList(block: DocBlock, visit: (runs: InlineContent[]) => void): void {
  const content = block.content
  if (!content) return
  if (isInlineList(content)) {
    visit(content)
    return
  }
  if (typeof content === 'object' && (content as { type?: string }).type === 'tableContent') {
    const rows = ((content as { rows?: unknown[] }).rows ?? []) as Array<{ cells?: unknown[] }>
    for (const row of rows) {
      for (const cell of row.cells ?? []) {
        if (isInlineList(cell)) visit(cell)
        else if (cell && isInlineList((cell as { content?: unknown }).content))
          visit((cell as { content: InlineContent[] }).content)
      }
    }
  }
}

/**
 * The block with every inline-content list of its own content (paragraph
 * text, each table cell) passed through `fn`; children untouched. Returns
 * the same object when the block has no inline content.
 */
export function mapInlineLists(
  block: DocBlock,
  fn: (runs: InlineContent[]) => InlineContent[]
): DocBlock {
  const content = block.content
  if (!content) return block
  if (isInlineList(content)) return { ...block, content: fn(content) as unknown as JsonValue }
  if (typeof content === 'object' && (content as { type?: string }).type === 'tableContent') {
    const table = content as { type: 'tableContent'; rows?: Array<{ cells?: unknown[] }> }
    const rows = (table.rows ?? []).map((row) => ({
      ...row,
      cells: (row.cells ?? []).map((cell) => {
        if (isInlineList(cell)) return fn(cell)
        if (cell && isInlineList((cell as { content?: unknown }).content))
          return {
            ...(cell as object),
            content: fn((cell as { content: InlineContent[] }).content),
          }
        return cell
      }),
    }))
    return { ...block, content: { ...table, rows } as unknown as JsonValue }
  }
  return block
}
